<template>
  <section class="flex flex-col h-full mx-4">
    <BaseDrawerConfirm
      :is-active="unsavedChangesDrawer"
      @confirm="onDiscardChanges"
      @cancel="unsavedChangesDrawer = false" />
    <BaseDetailsPreviewEditHeader
      :title="route?.name || ''"
      link-name="routes-management"
      hide-layout-options
      :mode="mode"
      :is-edit-active="route?.isFromManager"
      @edit-mode="mode = 'edit'"
      @cancel="onCancelEdit"
      @save="onSaveChanges" />
    <BaseTable
      :headers="modifiedRouteTraderSitesHeaders"
      :list="modifiedRouteTraderSites"
      :hide-grouping="true"
      :is-add-new-hidden="mode == 'view'"
      :hideMultiselectDelete="mode == 'view'"
      :addButtonText="$t('routes.addTraderSites')"
      @add-new="onAddNewRouteTraderSite"
      @deleteMultiselect="onDeleteMultiselect"
      @refresh-data="onRefreshData"
      table-id="modifiedRouteTraderSites"
      clickable
      @table-item-clicked="onEditRow"
      @emit-selected-items="onChooseVisitPlanForMultiSelection">
      <template #controls>
        <button
          class="px-2 py-1 text-sm font-normal capitalize border rounded-md border-cyan-light bg-cyan-light text-theme-500"
          @click="modalWeekPlan = true">
          <span>
            {{ $t('routes.viewWeekPlan') }}
          </span>
        </button>
      </template>
      <template #actions="{ item }: any">
        <div class="flex items-center justify-center gap-1">
          <ActionButton
            v-if="!item.deleted"
            :isEnabled="item.isFromManager && mode == 'edit'"
            color="red"
            :icon="IconTrashOutline"
            @action="onDeleteRow(item as RouteTraderSite)" />
          <ActionButton
            v-else
            :isEnabled="item.isFromManager && mode == 'edit'"
            :icon="IconTrashCancelOutline"
            @action="onRestoreDeleteRow(item as RouteTraderSite)" />
        </div>
      </template>

      <template v-if="mode == 'edit'" #extraButtonContent>
        <span> {{ $t('visitPlans.visitPlans') }} </span>
        <Icon icon="material-symbols:add-circle-outline-rounded" class="w-5 h-5 cursor-pointer" />
      </template>
    </BaseTable>

    <RouteWeekPlanModal
      v-if="modalWeekPlan"
      :mode="mode"
      :title="route?.name || ''"
      v-model="modifiedRouteTraderSites"
      @save="modalWeekPlan = false"
      @close="modalWeekPlan = false" />

    <RouteTraderSiteModalEdit
      v-if="editModalOpen"
      mode="edit"
      :route-trader-site="routeTraderSite"
      @close-modal="editModalOpen = false"
      @save="onModalSaveVisitPlan" />

    <RouteTraderSitesModalAdd
      v-if="sideBladeOpen"
      mode="add"
      :route-id="String(router.params.id)"
      @close="sideBladeOpen = false"
      @save="onAddTraderSites"></RouteTraderSitesModalAdd>

    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle">
      <template #text>
        <span v-if="deleteRowItem?.recordMode == RecordMode.Added">
          {{ $t('messages.info.deleteAdded', { item: deleteRowItem?.traderSite?.name }) }}
        </span>
        <span v-else-if="deleteRowItem?.recordMode == RecordMode.Modified">
          {{ $t('messages.info.deleteModified', { item: deleteRowItem?.traderSite?.name }) }}
        </span>
        <span v-else>
          {{ $t('messages.info.deleteSingle', { item: deleteRowItem?.traderSite?.name }) }}
        </span>
      </template>
    </BaseModalConfirm>

    <BaseModalConfirm
      v-if="modalRestoreRow"
      @cancel="modalRestoreRow = false"
      @confirm="onRestoreSingle">
      <template #title> {{ $t('common.actions.restore') }} </template>
      <template #text>
        {{ $t('messages.info.restoreSingle', { item: restoredRowItem?.traderSite?.name }) }}
      </template>
      <template #buttonText>{{ $t('common.actions.restore') }}</template>
    </BaseModalConfirm>
  </section>
</template>

<script setup lang="ts">
import BaseDetailsPreviewEditHeader from '@/components/common/BaseDetailsPreviewEditHeader.vue';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseDrawerConfirm from '@/components/common/BaseDrawerConfirm.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import IconTrashCancelOutline from '@/assets/icons/IconTrashCancelOutline.vue';
import ActionButton from '@/components/common/ActionButton.vue';
import {
  addEditRouteTraderSites,
  getRouteById,
  getRouteTraderSites,
  removeRouteTraderSitesByIds,
  restoreRouteTraderSiteById,
} from '@/services/api/routes';
import { RecordMode, type TableFilterData } from '@/types/Base';
import type { Route, RouteTraderSite } from '@/types/Routes';
import type { TraderSiteWithBaseCustomer } from '@/types/Traders';
import { filterData, sortData } from '@/utils/table';
import { deepCopy } from '@/utils/data';
import { onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { v4 as uuidv4 } from 'uuid';
import { useToastStore } from '@/stores/toast';
import RouteTraderSiteModalEdit from '@/components/routes/RouteTraderSiteModalEdit.vue';
import RouteTraderSitesModalAdd from '@/components/routes/RouteTraderSitesModalAdd.vue';
import RouteWeekPlanModal from '@/components/routes/RouteWeekPlanModal.vue';
import { Icon } from '@iconify/vue';

const { t: $t } = useI18n({ useScope: 'global' });

const toastStore = useToastStore();

interface Header {
  value: string;
  text: string;
  type?: string;
  cellStatusActiveText?: string | undefined;
  cellStatusColor?: 'green' | 'red' | 'blue' | undefined;
  active: boolean;
}
const modifiedRouteTraderSitesHeaders: Header[] = [
  { value: 'traderSite.code', text: $t('routes.siteCode'), type: 'text', active: true },
  { value: 'traderSite.name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'customer.code', text: $t('routes.customerCode'), type: 'text', active: true },
  {
    value: 'customer.profession',
    text: $t('routes.customerProfession'),
    type: 'text',
    active: true,
  },
  { value: 'traderSite.address', text: $t('address'), type: 'text', active: true },
  { value: 'traderSite.city', text: $t('city'), type: 'text', active: true },
  { value: 'traderSite.zipCode', text: $t('zipCode'), type: 'text', active: true },
  {
    value: 'basicRoute',
    text: $t('routes.typeOfRoute'),
    type: 'status',
    cellStatusActiveText: 'Basic',
    cellStatusColor: 'blue',
    active: true,
  },
  {
    value: 'isFromManager',
    text: $t('baseObject.isFromManager'),
    type: 'status',
    active: true,
  },
  { value: 'visitPlan.name', text: $t('visitPlans.visitPlan'), type: 'text', active: true },
  {
    value: 'deleted',
    text: $t('baseObject.isActive'),
    type: 'status',
    cellStatusActiveText: 'Deleted',
    cellStatusColor: 'red',
    active: true,
  },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

const mode = ref<'edit' | 'view'>('view');
const router = useRoute();
const modifiedRouteTraderSites = ref<RouteTraderSite[]>([]);
const routeTraderSites = ref<RouteTraderSite[]>([]);
const route = ref<Partial<Route> | null>();

onMounted(async () => {
  route.value = await getRouteById(router.params.id as string);
  await loadData('deleted', true);
});

async function loadData(sortColumn: string, sortAscending: boolean, query?: string) {
  let res = await getRouteTraderSites(router.params.id as string);
  if (query) {
    res = filterData(res, query, [
      'traderSite.name',
      'traderSite.code',
      'traderSite.address',
      'traderSite.city',
      'traderSite.zipCode',
      'visitPlan.name',
    ]);
  }

  if (sortColumn !== undefined && sortAscending !== undefined) {
    const colIndex = modifiedRouteTraderSitesHeaders.findIndex((x) => x.value == sortColumn);
    res = sortData(res, colIndex, sortAscending ? 'asc' : 'desc', modifiedRouteTraderSitesHeaders);
  }
  routeTraderSites.value = res;
  modifiedRouteTraderSites.value = deepCopy<RouteTraderSite[]>(res);
}

async function onRefreshData(data: TableFilterData) {
  const sortColumn =
    data.column !== undefined ? modifiedRouteTraderSitesHeaders[data.column]?.value : 'deleted';
  const sortAscending = data.order === 'asc' || data.order === undefined;
  await loadData(sortColumn, sortAscending);
}

async function onAddTraderSites(traderSites: TraderSiteWithBaseCustomer[]) {
  sideBladeOpen.value = false;
  traderSites
    .filter((x) => !modifiedRouteTraderSites.value.some((r) => r.traderSiteId == x.traderSite.id))
    .forEach((ts) => {
      const newRouteTraderSite: RouteTraderSite = {
        id: uuidv4(),
        isActive: true,
        isFromManager: true,
        routeId: router.params.id as string,
        traderSiteId: ts.traderSite.id,
        traderSite: ts.traderSite,
        customerId: ts.customer.id,
        customer: ts.customer,
        basicRoute: true,
        recordMode: RecordMode.Added,
      };
      modifiedRouteTraderSites.value.push(newRouteTraderSite);
    });
}

function onModalSaveVisitPlan(item: RouteTraderSite) {
  editModalOpen.value = false;
  multiTraderSitesSelected.value
    ? updateVisitPlanOnMultiSelect(item)
    : updateVisitPlanOnSingleRecord(item);
}

function updateVisitPlanOnSingleRecord(item: RouteTraderSite) {
  if (!routeTraderSite.value) return;
  routeTraderSite.value.visitPlanId = item.visitPlanId;
  routeTraderSite.value.visitPlan = item.visitPlan;

  if (!routeTraderSite.value.recordMode) routeTraderSite.value.recordMode = RecordMode.Modified;
  const index = modifiedRouteTraderSites.value.findIndex((x) => x.id == item.id);
  modifiedRouteTraderSites.value[index] = {
    ...modifiedRouteTraderSites.value[index],
    ...routeTraderSite.value,
  };
}

function updateVisitPlanOnMultiSelect(item: RouteTraderSite) {
  traderSitesUpdateVisitPlan.value.forEach((traderSite) => {
    const index = modifiedRouteTraderSites.value.findIndex((x) => x.id === traderSite.id);
    if (index === -1) return;
    modifiedRouteTraderSites.value[index] = {
      ...modifiedRouteTraderSites.value[index],
      visitPlanId: item.visitPlanId,
      visitPlan: item.visitPlan,
      recordMode: traderSite.recordMode ?? RecordMode.Modified,
    };
  });
}

async function onSaveChanges() {
  mode.value = 'view';
  const addUpdateRes = await addOrUpdate(
    modifiedRouteTraderSites.value.filter(
      (x) => x.recordMode == RecordMode.Added || x.recordMode == RecordMode.Modified
    )
  );

  const deleteRes = await deleteByIds(
    modifiedRouteTraderSites.value
      .filter((x) => x.recordMode == RecordMode.Deleted)
      .map((x) => x.id)
  );

  const restoreRes = await restoreByIds(
    modifiedRouteTraderSites.value
      .filter((x) => x.recordMode == RecordMode.Restored)
      .map((x) => x.id)
  );

  addUpdateRes && deleteRes && restoreRes
    ? toastStore.success({ text: $t('common.toast.success') })
    : toastStore.error({ text: $t('common.toast.error') });
  await loadData('deleted', true);
}

async function addOrUpdate(items: RouteTraderSite[]): Promise<boolean> {
  if (items.length <= 0) return true;
  return await addEditRouteTraderSites(items);
}

async function deleteByIds(ids: string[]): Promise<boolean> {
  if (ids.length <= 0) return true;
  const res = await removeRouteTraderSitesByIds(ids);
  return res ? false : true;
}

async function restoreByIds(ids: string[]): Promise<boolean> {
  if (ids.length <= 0) return true;
  const res = await restoreRouteTraderSiteById(ids);
  return res ? false : true;
}

function onCancelEdit() {
  const hasPendingChanges = modifiedRouteTraderSites.value.some(
    (x) =>
      x.recordMode == RecordMode.Added ||
      x.recordMode == RecordMode.Deleted ||
      x.recordMode == RecordMode.Restored ||
      x.recordMode == RecordMode.Modified
  );
  if (hasPendingChanges) {
    unsavedChangesDrawer.value = true;
  } else {
    mode.value = 'view';
  }
}

const unsavedChangesDrawer = ref(false);

async function onDiscardChanges() {
  modifiedRouteTraderSites.value = deepCopy<RouteTraderSite[]>(routeTraderSites.value);
  unsavedChangesDrawer.value = false;
  mode.value = 'view';
}

const sideBladeOpen = ref(false);
function onAddNewRouteTraderSite() {
  sideBladeOpen.value = true;
}

const editModalOpen = ref(false);
const routeTraderSite = ref<RouteTraderSite>(initRouteTraderSite());

function initRouteTraderSite(): RouteTraderSite {
  return {
    id: uuidv4(),
    routeId: '',
    traderSiteId: '',
    customerId: '',
    basicRoute: false,
    isFromManager: true,
  };
}

function onEditRow(item: RouteTraderSite) {
  if (mode.value == 'edit' && !item.deleted) {
    multiTraderSitesSelected.value = false;
    editModalOpen.value = true;
    routeTraderSite.value = item;
  }
}

const modalDeleteRow = ref(false);
const deleteRowItem = ref<RouteTraderSite>();
function onDeleteRow(item: RouteTraderSite) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}
async function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!deleteRowItem.value) return;

  if (deleteRowItem.value.recordMode == RecordMode.Added) {
    const index = modifiedRouteTraderSites.value.findIndex((x) => x.id == deleteRowItem.value?.id);
    modifiedRouteTraderSites.value.splice(index, 1);
    return;
  } else if (deleteRowItem.value.recordMode == RecordMode.Modified) {
    const index = modifiedRouteTraderSites.value.findIndex((x) => x.id == deleteRowItem.value?.id);
    const original = routeTraderSites.value.find((x) => x.id == deleteRowItem.value?.id);

    if (original) modifiedRouteTraderSites.value[index] = original;
  }
  const idToDelete = deleteRowItem.value.id;
  if (modifiedRouteTraderSites.value)
    updateLocalListDeleteStatuses([idToDelete], true, RecordMode.Deleted);
}
async function onDeleteMultiselect(items: RouteTraderSite[]) {
  items.forEach((item) => {
    if (!item.isFromManager) return;
    deleteRowItem.value = item;
    onDeleteSingle();
  });
}

const modalRestoreRow = ref(false);
const restoredRowItem = ref<RouteTraderSite>();
function onRestoreDeleteRow(item: RouteTraderSite) {
  restoredRowItem.value = item;
  modalRestoreRow.value = true;
}
async function onRestoreSingle() {
  modalRestoreRow.value = false;
  if (!restoredRowItem.value) return;
  const idToRestore = restoredRowItem.value.id;
  if (modifiedRouteTraderSites.value)
    updateLocalListDeleteStatuses([idToRestore], false, RecordMode.Restored);
}

const modalWeekPlan = ref(false);

function updateLocalListDeleteStatuses(ids: string[], isDeleted: boolean, recordMode: RecordMode) {
  ids.forEach((id) => {
    const index = modifiedRouteTraderSites.value.findIndex((x) => x.id == id);
    modifiedRouteTraderSites.value[index] = {
      ...modifiedRouteTraderSites.value[index],
      deleted: isDeleted,
      recordMode: recordMode,
    };
  });
}

const traderSitesUpdateVisitPlan = ref<RouteTraderSite[]>([]);
const multiTraderSitesSelected = ref(false);
function onChooseVisitPlanForMultiSelection(items: RouteTraderSite[]) {
  routeTraderSite.value = initRouteTraderSite();
  traderSitesUpdateVisitPlan.value = items.filter(
    (item) => item.isFromManager && item.recordMode !== RecordMode.Restored
  );

  if (!traderSitesUpdateVisitPlan.value.length) {
    toastStore.info({ text: $t('messages.info.selectedItemsCannotBeEdited') });
    return;
  }
  multiTraderSitesSelected.value = true;
  editModalOpen.value = true;
}
</script>
